import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchField from '../Forms/SearchField'
import { Observer } from 'mobx-react'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { useCancelToken } from '../../utils/hooks/useCancelToken'
import { makeCall } from '../../utils/api/makeCall'
import styled from 'styled-components'
import { MagnifyingGlassIcon } from '../Icons/MagnifyingGlassIcon'
import { colors } from '../../styles/colors'
import { BiBarcodeReader } from 'react-icons/bi'
import { useTranslation } from 'react-i18next'
import QrModal from '../Modals/QrModal'

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
`

const SearchIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`

const SearchIconGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  background: white;
  padding: 6px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease-in-out;
  transform: ${({ isOpen }) => (isOpen ? 'scale(1)' : 'scale(0)')};
  transform-origin: right center;
  z-index: 9999;
  width: 250px;

  input {
    width: 100%;
    font-size: 16px;
    height: 36px;
    border-radius: 6px;
    padding: 6px 10px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.gray2};
  font-size: 20px;
  cursor: pointer;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 50%;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`

const UniversalSearchBox = () => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isScanOpen, setIsScanOpen] = useState(false)
  const { createOrCancelToken, sourceRef, isCancel } = useCancelToken()
  const [, setErrorStates] = useState({})
  const [, setLoadingStates] = useState({})
  const navigate = useNavigate()
  const searchWrapperRef = useRef(null)

  const handleChangeSearchText = (e) => {
    const value = e.target.value
    setSearchText(value)
    startSearch(value)
  }

  const handleClearClick = () => {
    setSearchText('')
    searchWrapperRef.current?.querySelector('input')?.focus()
  }

  const startSearch = async (searchtext) => {
    if (!searchtext?.trim()) {
      return
    }

    createOrCancelToken()
    makeCall(
      'search',
      async () => {
        const response = await apiClient.getJson(
          APIEndpoints.search(searchtext),
          null,
          true,
          true,
          true,
          { cancelToken: sourceRef.current.token },
        )

        if (response.data && response.data.length) {
          setSearchText('')
          navigate(response.data[0].path)
          setIsSearchOpen(false)
        }
      },
      setErrorStates,
      setLoadingStates,
      isCancel,
    )
  }

  const handleScanResult = (result) => {
    setIsScanOpen(false)
    setSearchText(result)
    startSearch(result)
  }

  useEffect(() => {
    if (isSearchOpen) {
      searchWrapperRef.current?.querySelector('input')?.focus()
    } else {
      setSearchText('')
    }
  }, [isSearchOpen])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isSearchOpen &&
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      ) {
        setIsSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSearchOpen])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSearchOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <Observer>
      {() => (
        <SearchWrapper>
          <SearchIconGroup ref={searchWrapperRef}>
            <SearchIcon onClick={() => setIsSearchOpen(!isSearchOpen)}>
              <MagnifyingGlassIcon color={colors.gray2} />
            </SearchIcon>

            <SearchInputWrapper ref={searchWrapperRef} isOpen={isSearchOpen}>
              <SearchField
                placeholder='Search...'
                value={searchText}
                onChange={handleChangeSearchText}
                onClearClick={handleClearClick}
              />
              <CloseButton onClick={() => setIsSearchOpen(false)}>×</CloseButton>
            </SearchInputWrapper>
          </SearchIconGroup>

          <SearchIcon onClick={() => setIsScanOpen(true)}>
            <BiBarcodeReader color={colors.gray2} size={20} />
          </SearchIcon>

          {isScanOpen && (
            <QrModal
              title={t('inventory:searchScan')}
              handleClose={() => setIsScanOpen(false)}
              handleResult={handleScanResult}
            />
          )}
        </SearchWrapper>
      )}
    </Observer>
  )
}

export default UniversalSearchBox
